/*
 * Preloads a mobile hero image while it is still approaching the viewport,
 * recovers the desktop hero image if a selected mobile picture source fails,
 * and loads product secondary images only after a fine-pointer hover or focus.
 */
(() => {
  'use strict';

  if (window.ShopTheLookGrid) {
    window.ShopTheLookGrid.initializeAll();
    return;
  }

  const rootSelector = '[data-shop-the-look]';
  const linkSelector = '[data-shop-the-look-product-link]';
  const imageSelector = '[data-shop-the-look-secondary-image]';
  const heroImageSelector = '[data-shop-the-look-hero-image]';
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const mobileViewport = window.matchMedia('(max-width: 749px)');
  const heroPreloadObservers = new WeakMap();

  function selectSrcsetCandidate(srcset, targetWidth) {
    if (!srcset) return null;

    const candidates = srcset
      .split(',')
      .map((entry) => {
        const [url, descriptor] = entry.trim().split(/\s+/);
        const width = Number.parseInt(descriptor, 10);
        return { url, width };
      })
      .filter((candidate) => candidate.url && Number.isFinite(candidate.width));

    if (!candidates.length) return null;

    candidates.sort((first, second) => first.width - second.width);
    return candidates.find((candidate) => candidate.width >= targetWidth) || candidates[candidates.length - 1];
  }

  function preloadMobileHeroImage(image) {
    if (!image || image.dataset.shopTheLookMobilePreloadRequested === 'true') return;

    const picture = image.closest('picture');
    const mobileSource = picture && picture.querySelector('[data-shop-the-look-mobile-source]');

    if (!mobileSource || !window.matchMedia(mobileSource.media).matches) return;

    const imageWidth = image.getBoundingClientRect().width || window.innerWidth;
    const targetWidth = Math.ceil(imageWidth * (window.devicePixelRatio || 1));
    const candidate = selectSrcsetCandidate(mobileSource.srcset, targetWidth);

    if (!candidate) return;

    const preloader = new Image();
    preloader.decoding = 'async';

    preloader.addEventListener('load', () => {
      image.dataset.shopTheLookMobilePreloadReady = 'true';
    }, { once: true });

    image.dataset.shopTheLookMobilePreloadRequested = 'true';
    image._shopTheLookMobilePreloader = preloader;
    preloader.src = candidate.url;
  }

  function initializeMobileHeroPreload(root) {
    if (!root || !mobileViewport.matches || heroPreloadObservers.has(root)) return;

    const hero = root.querySelector('.shop-the-look__hero');
    if (!hero) return;

    const preload = () => {
      root.querySelectorAll(heroImageSelector).forEach(preloadMobileHeroImage);
    };

    if (!('IntersectionObserver' in window)) {
      preload();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      preload();
      observer.disconnect();
      heroPreloadObservers.delete(root);
    }, {
      // Starts a low-priority request well before this below-the-fold module is visible.
      rootMargin: '3000px 0px',
      threshold: 0,
    });

    observer.observe(hero);
    heroPreloadObservers.set(root, observer);
  }

  function initializeHeroImageFallback(root) {
    if (!root || root.dataset.shopTheLookHeroInitialized === 'true') return;

    root.dataset.shopTheLookHeroInitialized = 'true';

    root.querySelectorAll(heroImageSelector).forEach((image) => {
      const picture = image.closest('picture');
      const mobileSource = picture && picture.querySelector('[data-shop-the-look-mobile-source]');

      if (!picture || !mobileSource || !image.dataset.desktopSrc) return;

      let fallbackApplied = false;

      image.addEventListener('error', () => {
        if (fallbackApplied) return;

        fallbackApplied = true;
        mobileSource.remove();

        if (image.dataset.desktopSrcset) image.srcset = image.dataset.desktopSrcset;
        if (image.dataset.desktopSizes) image.sizes = image.dataset.desktopSizes;
        image.src = image.dataset.desktopSrc;
      });
    });

    initializeMobileHeroPreload(root);
  }

  function loadSecondaryImage(image) {
    if (!image || image.dataset.loaded === 'true' || !image.dataset.src) return;

    const imageWrap = image.closest('.shop-the-look__product-image-wrap');

    image.addEventListener(
      'load',
      () => {
        image.classList.add('is-loaded');
        if (imageWrap) imageWrap.classList.add('has-secondary-loaded');
      },
      { once: true }
    );

    image.dataset.loaded = 'true';
    image.src = image.dataset.src;

    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    if (image.dataset.sizes) image.sizes = image.dataset.sizes;
  }

  function initialize(root) {
    if (!root) return;

    initializeHeroImageFallback(root);

    if (!finePointer.matches || root.dataset.shopTheLookHoverInitialized === 'true') return;
    root.dataset.shopTheLookHoverInitialized = 'true';

    root.querySelectorAll(linkSelector).forEach((link) => {
      const secondaryImage = link.querySelector(imageSelector);
      if (!secondaryImage) return;

      link.addEventListener(
        'pointerenter',
        () => {
          if (finePointer.matches) loadSecondaryImage(secondaryImage);
        },
        { once: true }
      );

      link.addEventListener(
        'focusin',
        () => {
          if (finePointer.matches) loadSecondaryImage(secondaryImage);
        },
        { once: true }
      );
    });
  }

  function initializeAll(container = document) {
    if (container.matches && container.matches(rootSelector)) initialize(container);
    container.querySelectorAll(rootSelector).forEach(initialize);
  }

  window.ShopTheLookGrid = { initializeAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.ShopTheLookGrid.initializeAll(), { once: true });
  } else {
    window.ShopTheLookGrid.initializeAll();
  }

  const reinitializeIfFinePointer = (event) => {
    if (event.matches) window.ShopTheLookGrid.initializeAll();
  };

  if (finePointer.addEventListener) {
    finePointer.addEventListener('change', reinitializeIfFinePointer);
  } else if (finePointer.addListener) {
    finePointer.addListener(reinitializeIfFinePointer);
  }

  document.addEventListener('shopify:section:load', (event) => {
    window.ShopTheLookGrid.initializeAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const roots = [];

    if (event.target.matches && event.target.matches(rootSelector)) roots.push(event.target);
    if (event.target.querySelectorAll) roots.push(...event.target.querySelectorAll(rootSelector));

    roots.forEach((root) => {
      heroPreloadObservers.get(root)?.disconnect();
      heroPreloadObservers.delete(root);
    });
  });
})();
